import { createContext, useContext, useEffect, useState } from 'react';
import {
  subscribeAuth,
  checkRefreshToken,
  loginUser,
  logoutUser,
  registerUser,
  getTokensFromCookies,
  getCookie,
} from '../api/AuthApi';
import { getPayload } from '../utils/getPayload';
import { deleteCookie } from '../utils/utils';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);

  const token_a = getCookie('a_token');
  const { is_admin } = token_a ? getPayload(token_a) : { is_admin: null };


  // --- Ловим GET-параметры при загрузке ---
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const from = query.get('from'); // например "messenger"
    if (from) setRedirectAfterLogin(from);
  }, []);

  // --- Проверка токена при загрузке страницы ---
  useEffect(() => {
    (async () => {
      const result = await checkRefreshToken();
      setInitialized(true);
      setIsAuthenticated(result);

      // Если уже аутентифицированы и есть редирект
      if (result && redirectAfterLogin) {
        handleRedirect(redirectAfterLogin);
      }
    })();
  }, [redirectAfterLogin]);

  // --- Подписка на изменения состояния аутентификации ---
  useEffect(() => {
    const unsubscribe = subscribeAuth(setIsAuthenticated);
    return unsubscribe;
  }, []);

  // --- Разлогинить если 401 ошибка при любом запросе ---
  useEffect(() => {
    const handler = async () => {
      deleteCookie('current_organisation');
      deleteCookie('a_token'); // <-- важно
      deleteCookie('r_token'); // <-- если есть
      deleteCookie('auth_authorized'); // <-- если есть
      deleteCookie('auth_user'); // <-- если есть
      deleteCookie('user_uuid'); // <-- если есть
      localStorage.clear();
      sessionStorage.clear();
      setIsAuthenticated(false);
    };


    window.addEventListener('force-logout', handler);
    return () => window.removeEventListener('force-logout', handler);
  }, []);


  // --- Маппинг коротких меток на реальные URL ---
  const redirectMap = {
    messenger: window.APP_CONFIG?.MESSENGER_DOMAIN,
    crm: window.APP_CONFIG?.CRM_DOMAIN,
    market: window.APP_CONFIG?.MARKET_DOMAIN,
    lk: window.APP_CONFIG?.LK_DOMAIN,
  };

  const handleRedirect = (key) => {
    const url = redirectMap[key];
    if (url) {
      window.location.href = url;
    }
  };

  const login = async (loginData) => {
    const { login: userLogin, password } = loginData;
    const res = await loginUser(userLogin, password);
    if (res.status === 'Successfully') {
      setIsAuthenticated(true);

      // Делаем редирект после успешного логина
      if (redirectAfterLogin) {
        handleRedirect(redirectAfterLogin);
      }
    }
  };

  const logout = async () => {
    try {
      const { a_token } = getTokensFromCookies();
      if (a_token) {
        await logoutUser({ a_token });
      }
    } catch (e) {
      console.error('Ошибка при logout:', e);
    }
    deleteCookie('a_token'); // <-- важно
    deleteCookie('r_token'); // <-- если есть
    deleteCookie('current_organisation');
    deleteCookie('auth_authorized'); // <-- если есть
    deleteCookie('auth_user'); // <-- если есть
    deleteCookie('user_uuid'); // <-- если есть
    localStorage.clear();
    sessionStorage.clear();
    setIsAuthenticated(false);
  };

  const register = async (registerData) => {
    const { email, password, phone, login: userLogin } = registerData;
    await registerUser({ email, password, phone, login: userLogin });
  };

  if (!initialized) return null;

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, register, is_admin }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
